/**
 * Servicio para sincronización de configuración de usuario con AWS
 * Gestiona preferencias de tarifa, miembros del hogar, etc.
 */

import api from './api';
import authService from './auth';

class UserConfigService {
  /**
   * Obtiene las preferencias del usuario desde AWS
   */
  async getUserPreferences() {
    // La demostración no tiene token de Cognito: llamar a la API real sólo
    // produce un 401 y ensucia la consola. Sus preferencias viven en local.
    if (authService.isDemo?.()) return this.getLocalPreferences();
    try {
      const response = await api.get('/preferences');
      return response.data;
    } catch (error) {
      console.error('Error getting user preferences from AWS:', error);
      // Fallback a localStorage
      return this.getLocalPreferences();
    }
  }

  /**
   * Actualiza las preferencias del usuario en AWS
   */
  async updateUserPreferences(preferences) {
    if (authService.isDemo?.()) {
      this.saveLocalPreferences(preferences);
      return preferences;
    }
    try {
      const response = await api.put('/preferences', preferences);
      
      // Actualizar localStorage también
      this.saveLocalPreferences(preferences);
      
      return response.data;
    } catch (error) {
      console.error('Error updating preferences in AWS:', error);
      
      // Guardar localmente aunque falle AWS
      this.saveLocalPreferences(preferences);
      
      throw error;
    }
  }

  /**
   * Sincroniza preferencias al iniciar sesión
   */
  async syncPreferences() {
    try {
      if (!authService.isAuthenticated() || authService.isDemo?.()) {
        return this.getLocalPreferences();
      }

      const remotePrefs = await this.getUserPreferences();
      const localPrefs = this.getLocalPreferences();
      
      // Si hay preferencias locales más recientes, subir a AWS
      const localTimestamp = localStorage.getItem('preferencesUpdatedAt');
      const remoteTimestamp = remotePrefs.updated_at || 0;
      
      if (localTimestamp && parseInt(localTimestamp) > remoteTimestamp) {
        await this.updateUserPreferences(localPrefs);
        return localPrefs;
      } else {
        this.saveLocalPreferences(remotePrefs);
        return remotePrefs;
      }
    } catch (error) {
      console.error('Error syncing preferences:', error);
      return this.getLocalPreferences();
    }
  }

  /**
   * Obtiene preferencias desde localStorage
   */
  getLocalPreferences() {
    return {
      selected_tariff: localStorage.getItem('selectedTariff') || 'domestic',
      household_members: parseInt(localStorage.getItem('householdMembers')) || 1,
      daily_goal: localStorage.getItem('dailyGoal') ? parseInt(localStorage.getItem('dailyGoal')) : '',
      selected_unit: localStorage.getItem('selectedUnit') || 'L'
    };
  }

  /**
   * Guarda preferencias en localStorage
   */
  saveLocalPreferences(preferences) {
    if (preferences.selected_tariff !== undefined) {
      localStorage.setItem('selectedTariff', preferences.selected_tariff);
    }
    if (preferences.household_members !== undefined && preferences.household_members !== null) {
      localStorage.setItem('householdMembers', String(preferences.household_members));
    }
    // "sin meta" viaja como null (UserConfigContext lo convierte desde la
    // cadena vacía del formulario) y antes reventaba aquí con
    // `null.toString()`, abortando el guardado entero. Ausencia de meta se
    // guarda borrando la clave, no escribiendo "null".
    if (preferences.daily_goal !== undefined) {
      if (preferences.daily_goal === null || preferences.daily_goal === '') {
        localStorage.removeItem('dailyGoal');
      } else {
        localStorage.setItem('dailyGoal', String(preferences.daily_goal));
      }
    }
    if (preferences.selected_unit !== undefined) {
      localStorage.setItem('selectedUnit', preferences.selected_unit);
    }
    
    localStorage.setItem('preferencesUpdatedAt', Date.now().toString());
  }

  /**
   * Limpia las preferencias locales
   */
  clearLocalPreferences() {
    localStorage.removeItem('selectedTariff');
    localStorage.removeItem('householdMembers');
    localStorage.removeItem('dailyGoal');
    localStorage.removeItem('selectedUnit');
    localStorage.removeItem('preferencesUpdatedAt');
  }
}

export const userConfigService = new UserConfigService();
export default userConfigService;

